import { Bot, ClipboardPaste, FileText, GitCompare, Rocket, Search, Shield, Upload } from 'lucide-react';

import type { NovaSkill } from '../queries/chats';

export interface SkillCommand {
  id: string;
  handle: string;
  description: string;
  category: string;
  title?: string;
  exampleUsage?: string;
  instructionPlaceholder?: string;
  skill?: NovaSkill;
}

export interface SkillCommandGroup {
  category: string;
  commands: SkillCommand[];
}

/**
 * Map skill categories to Lucide icons
 */
export const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  GET: Search,
  FILL: FileText,
  REQUEST: Upload,
  INGEST: ClipboardPaste,
  CREATE: FileText,
  COMPARE: GitCompare,
  AUDIT: Shield,
  NEW: Rocket,
  SKILL: Bot,
};

/**
 * Map skill categories to human-readable display names
 */
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  GET: 'Get',
  FILL: 'Fill',
  REQUEST: 'Request',
  INGEST: 'Ingest',
  CREATE: 'Create',
  COMPARE: 'Compare',
  AUDIT: 'Audit',
  NEW: "What's New",
  SKILL: 'Skills',
};

export const OTHER_SKILL_CATEGORY = 'Other';

function getSkillCategory(skill: NovaSkill): string {
  return skill.category?.trim() || OTHER_SKILL_CATEGORY;
}

export function buildSkillCommand(skill: NovaSkill): SkillCommand {
  // Only set title when it differs from the handle to avoid duplicate display
  const title = skill.name !== skill.id ? skill.name : undefined;

  return {
    id: `skill:${skill.id}`,
    handle: skill.id,
    title,
    description: skill.description ?? '',
    category: getSkillCategory(skill),
    exampleUsage: skill.exampleUsage ?? undefined,
    instructionPlaceholder: skill.instructionPlaceholder ?? undefined,
    skill,
  };
}

export function buildSkillCommands(skills: NovaSkill[]): SkillCommand[] {
  return [...skills].map(buildSkillCommand).sort((left, right) => {
    if (left.category !== right.category) {
      if (left.category === OTHER_SKILL_CATEGORY) {
        return 1;
      }
      if (right.category === OTHER_SKILL_CATEGORY) {
        return -1;
      }
      return left.category.localeCompare(right.category);
    }

    return (left.title ?? left.handle).localeCompare(right.title ?? right.handle);
  });
}

export function groupSkillCommandsByCategory(commands: SkillCommand[]): SkillCommandGroup[] {
  const groups = new Map<string, SkillCommand[]>();

  commands.forEach((command) => {
    const existingGroup = groups.get(command.category);
    if (existingGroup) {
      existingGroup.push(command);
      return;
    }

    groups.set(command.category, [command]);
  });

  return Array.from(groups.entries(), ([category, categoryCommands]) => ({
    category,
    commands: categoryCommands,
  }));
}

export function getSkillCommandIcon(command: SkillCommand): React.ComponentType<{ className?: string }> {
  return CATEGORY_ICONS[command.category] || CATEGORY_ICONS.SKILL || FileText;
}
